import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Paper, Switch, FormControlLabel, TextField, IconButton, List, ListItem, ListItemButton, Typography, Popper, useMediaQuery, } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getLocalTimeZone, today, CalendarDate, startOfMonth } from '@internationalized/date';
import AppButton from '../AppButton';
import './CustomDatePicker.css';
/**
 * Month picker that mirrors the CustomDatePicker layout (presets sidebar +
 * Compare toggle + Vs compare box + Cancel/Apply) but selects a SINGLE month
 * per box instead of a date range.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CalendarSvg = () => (_jsx("svg", { className: "calendar-icon", width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M12.75 1.5C12.75 1.08579 12.4142 0.75 12 0.75C11.5858 0.75 11.25 1.08579 11.25 1.5H12H12.75ZM11.25 4.5C11.25 4.91421 11.5858 5.25 12 5.25C12.4142 5.25 12.75 4.91421 12.75 4.5H12H11.25ZM6.75 1.5C6.75 1.08579 6.41421 0.75 6 0.75C5.58579 0.75 5.25 1.08579 5.25 1.5H6H6.75ZM5.25 4.5C5.25 4.91421 5.58579 5.25 6 5.25C6.41421 5.25 6.75 4.91421 6.75 4.5H6H5.25ZM2.25 6.75C1.83579 6.75 1.5 7.08579 1.5 7.5C1.5 7.91421 1.83579 8.25 2.25 8.25V7.5V6.75ZM15.75 8.25C16.1642 8.25 16.5 7.91421 16.5 7.5C16.5 7.08579 16.1642 6.75 15.75 6.75V7.5V8.25ZM3.75 3V3.75H14.25V3V2.25H3.75V3ZM14.25 3V3.75C14.6642 3.75 15 4.08579 15 4.5H15.75H16.5C16.5 3.25736 15.4926 2.25 14.25 2.25V3ZM15.75 4.5H15V15H15.75H16.5V4.5H15.75ZM15.75 15H15C15 15.4142 14.6642 15.75 14.25 15.75V16.5V17.25C15.4926 17.25 16.5 16.2426 16.5 15H15.75ZM14.25 16.5V15.75H3.75V16.5V17.25H14.25V16.5ZM3.75 16.5V15.75C3.33579 15.75 3 15.4142 3 15H2.25H1.5C1.5 16.2426 2.50736 17.25 3.75 17.25V16.5ZM2.25 15H3V4.5H2.25H1.5V15H2.25ZM2.25 4.5H3C3 4.08579 3.33579 3.75 3.75 3.75V3V2.25C2.50736 2.25 1.5 3.25736 1.5 4.5H2.25ZM12 1.5H11.25V4.5H12H12.75V1.5H12ZM6 1.5H5.25V4.5H6H6.75V1.5H6ZM2.25 7.5V8.25H15.75V7.5V6.75H2.25V7.5Z", fill: "#71717A" }) }));
const formatMonth = (m) => m ? `${MONTHS[m.month - 1]} ${m.year}` : '';
const sameMonth = (a, b) => !!a && !!b && a.year === b.year && a.month === b.month;
// The compare month is considered "custom" (admin-overridden) when it is NOT
// exactly one month before the main month. Custom compare stays put when main changes.
const isCompareCustom = (main, compare) => {
    if (!main || !compare)
        return false;
    return !sameMonth(compare, startOfMonth(main.subtract({ months: 1 })));
};
const CustomMonthComparePicker = ({ value = null, compareValue = null, onApply, placeholder = 'Select Month', comparePlaceholder = 'Compare Month', comparison = true, disabled = false, maxPastYears = 100, futureMonths = 0, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectingCompare, setSelectingCompare] = useState(false);
    const [compareEnabled, setCompareEnabled] = useState(!!compareValue);
    const [tempMain, setTempMain] = useState(value);
    const [tempCompare, setTempCompare] = useState(compareValue);
    // "Custom" is tracked per box so a manual pick that matches no preset stays highlighted.
    // compare = true means the admin decoupled the compare month → main changes won't shift it.
    const [customPicked, setCustomPicked] = useState({ main: false, compare: isCompareCustom(value, compareValue) });
    const now = today(getLocalTimeZone());
    const [navYear, setNavYear] = useState(() => (value?.year ?? now.year));
    const anchorRef = useRef(null);
    const popperRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const minYear = now.year - maxPastYears;
    // Year nav stops at the year holding the last selectable month, so a negative
    // allowance in January doesn't leave a fully disabled year reachable.
    const maxYear = futureMonths === 'ALL'
        ? now.year + 100
        : now.add({ months: typeof futureMonths === 'number' ? futureMonths : 0 }).year;
    /* ── keep temp state in sync with props ── */
    useEffect(() => {
        setTempMain(value);
        if (value?.year)
            setNavYear(value.year);
    }, [value]);
    useEffect(() => {
        setTempCompare(compareValue);
        setCompareEnabled(!!compareValue && comparison);
    }, [compareValue, comparison]);
    useEffect(() => {
        if (!comparison) {
            setCompareEnabled(false);
            setTempCompare(null);
            setSelectingCompare(false);
            setCustomPicked((p) => ({ ...p, compare: false }));
        }
    }, [comparison]);
    // futureMonths is the allowance beyond the current month: 0 = current month is
    // the latest selectable, negative values push the cut-off back (-1 disables the
    // current month too), 'ALL' removes the cut-off entirely.
    const isMonthDisabled = (year, monthIdx) => {
        if (futureMonths === 'ALL')
            return false;
        const target = year * 12 + (monthIdx + 1);
        const current = now.year * 12 + now.month;
        const allowance = typeof futureMonths === 'number' ? futureMonths : 0;
        return target > current + allowance;
    };
    const isPresetMonthDisabled = (preset) => {
        const m = preset.getMonth();
        return !!m && isMonthDisabled(m.year, m.month - 1);
    };
    /* ── presets (single month) ── */
    const monthFromNow = (monthsAgo) => startOfMonth(now.subtract({ months: monthsAgo }));
    const allMainPresets = [
        { label: 'This Month', getMonth: () => monthFromNow(0) },
        { label: 'Last Month', getMonth: () => monthFromNow(1) },
        { label: '2 Months Ago', getMonth: () => monthFromNow(2) },
        { label: '3 Months Ago', getMonth: () => monthFromNow(3) },
        { label: '6 Months Ago', getMonth: () => monthFromNow(6) },
        { label: 'Previous Year', getMonth: () => monthFromNow(12) },
    ];
    // Presets that resolve to a month past the cut-off are dropped from the sidebar
    // (e.g. "This Month" when the current month isn't selectable).
    const mainPresets = allMainPresets.filter((p) => !isPresetMonthDisabled(p));
    const allComparePresets = [
        {
            label: 'Previous Month',
            requiresMain: true,
            getMonth: () => (tempMain ? startOfMonth(tempMain.subtract({ months: 1 })) : null),
        },
        {
            label: 'Previous Year',
            requiresMain: true,
            getMonth: () => (tempMain ? startOfMonth(tempMain.subtract({ months: 12 })) : null),
        },
        // {
        //     label: 'Same Month Next Year',
        //     requiresMain: true,
        //     getMonth: () => (tempMain ? startOfMonth(tempMain.add({ months: 12 })) : null),
        // },
        { label: 'Custom', getMonth: () => null, isCustom: true },
    ];
    const comparePresets = allComparePresets.filter((p) => !isPresetMonthDisabled(p));
    const presets = selectingCompare && compareEnabled ? comparePresets : mainPresets;
    // A preset is "active" when the current box's selected month equals the preset's month.
    // A manual pick that matches no preset falls back to highlighting "Custom".
    const isPresetActive = (preset) => {
        const isCompareCtx = selectingCompare && compareEnabled;
        const current = isCompareCtx ? tempCompare : tempMain;
        const custom = isCompareCtx ? customPicked.compare : customPicked.main;
        if (preset.isCustom) {
            const anyNamedMatch = presets.some((p) => !p.isCustom && sameMonth(p.getMonth(), current));
            return custom && !anyNamedMatch;
        }
        return sameMonth(preset.getMonth(), current);
    };
    /* ── open / close ── */
    const handleOpen = (compareBox = false) => {
        setIsOpen(true);
        setSelectingCompare(compareBox && compareEnabled);
        const active = compareBox && compareEnabled ? tempCompare : tempMain;
        if (active?.year)
            setNavYear(active.year);
    };
    const handleClose = useCallback(() => {
        setIsOpen(false);
        setTempMain(value);
        setTempCompare(compareValue);
        setCompareEnabled(!!compareValue);
        setSelectingCompare(false);
        setCustomPicked({ main: false, compare: isCompareCustom(value, compareValue) });
    }, [value, compareValue]);
    useEffect(() => {
        const onClickOutside = (e) => {
            if (!isOpen || isMobile)
                return;
            const t = e.target;
            if (!anchorRef.current?.contains(t) && !popperRef.current?.contains(t))
                handleClose();
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [isOpen, isMobile, handleClose]);
    /* ── actions ── */
    const handleApply = () => {
        onApply?.({ main: tempMain, compare: compareEnabled ? tempCompare : null });
        setIsOpen(false);
        setSelectingCompare(false);
    };
    const handleCompareToggle = (e) => {
        if (!comparison)
            return;
        const enabled = e.target.checked;
        setCompareEnabled(enabled);
        if (!enabled) {
            setTempCompare(null);
            setSelectingCompare(false);
            setCustomPicked((p) => ({ ...p, compare: false }));
            return;
        }
        // Default compare → previous month (one month less than the main selection).
        if (tempMain) {
            const def = startOfMonth(tempMain.subtract({ months: 1 }));
            setTempCompare(def);
            setCustomPicked((p) => ({ ...p, compare: false }));
            setNavYear(def.year);
        }
        setSelectingCompare(true);
    };
    const handlePresetClick = (preset) => {
        if (isPresetMonthDisabled(preset))
            return;
        if (selectingCompare && compareEnabled) {
            if (preset.isCustom) {
                setCustomPicked((p) => ({ ...p, compare: true }));
                return;
            }
            if (preset.requiresMain && !tempMain)
                return;
            const m = preset.getMonth();
            if (m) {
                setTempCompare(m);
                // "Previous Month" re-links to the auto (main − 1) rule; any other
                // explicit compare preset decouples so main changes won't override it.
                const relink = preset.label === 'Previous Month';
                setCustomPicked((p) => ({ ...p, compare: !relink }));
                setNavYear(m.year);
            }
        }
        else {
            const m = preset.getMonth();
            if (m) {
                setTempMain(m);
                setCustomPicked((p) => ({ ...p, main: false }));
                setNavYear(m.year);
                // Default flow: compare follows main (main − 1) unless admin decoupled it.
                if (compareEnabled && !customPicked.compare) {
                    setTempCompare(startOfMonth(m.subtract({ months: 1 })));
                }
            }
        }
    };
    const handleMonthClick = (year, monthIdx) => {
        if (isMonthDisabled(year, monthIdx))
            return;
        const picked = new CalendarDate(year, monthIdx + 1, 1);
        if (selectingCompare && compareEnabled) {
            setTempCompare(picked);
            setCustomPicked((p) => ({ ...p, compare: true }));
        }
        else {
            setTempMain(picked);
            setCustomPicked((p) => ({ ...p, main: true }));
            // Default flow: compare follows main (main − 1) unless admin decoupled it.
            if (compareEnabled && !customPicked.compare) {
                setTempCompare(startOfMonth(picked.subtract({ months: 1 })));
            }
        }
    };
    const navYearShift = (dir) => {
        setNavYear((prev) => {
            const next = prev + dir;
            if (next < minYear || next > maxYear)
                return prev;
            return next;
        });
    };
    /* ── render one year panel (12-month grid) ── */
    const renderYearPanel = (panelYear, showPrev, showNext) => {
        // Highlight only the month for the box currently being edited, to avoid
        // showing both the main and compare month at once.
        const activeSel = selectingCompare && compareEnabled ? tempCompare : tempMain;
        return (_jsxs(Box, { className: "main-calendar-container", sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { className: "calendar-header", children: [showPrev ? (_jsx(IconButton, { size: "small", onClick: () => navYearShift(-1), disabled: navYear <= minYear, className: "calendar-nav-button", children: _jsx(ChevronLeft, {}) })) : (_jsx(Box, { sx: { width: 28, height: 28 } })), _jsx(Typography, { variant: "body1", className: "calendar-month-name", children: panelYear }), showNext ? (_jsx(IconButton, { size: "small", onClick: () => navYearShift(1), disabled: navYear >= maxYear, className: "calendar-nav-button", children: _jsx(ChevronRight, {}) })) : (_jsx(Box, { sx: { width: 28, height: 28 } }))] }), _jsx(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '6px',
                        pt: 1.5,
                    }, children: MONTHS.map((label, idx) => {
                        const isSelected = sameMonth(activeSel, new CalendarDate(panelYear, idx + 1, 1));
                        const isDisabled = isMonthDisabled(panelYear, idx);
                        return (_jsx(Box, { onClick: () => handleMonthClick(panelYear, idx), sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 1.25,
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'Inter',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                fontWeight: isSelected ? 600 : 400,
                                color: isDisabled ? '#D0D5DD' : isSelected ? '#fff' : '#18181B',
                                bgcolor: isSelected ? '#FF008E' : 'transparent',
                                '&:hover': {
                                    bgcolor: isDisabled ? 'transparent' : isSelected ? '#FF008E' : '#F5F5F5',
                                },
                            }, children: label }, label));
                    }) })] }));
    };
    const displayMain = formatMonth(value);
    const displayCompare = formatMonth(compareValue);
    return (_jsxs(Box, { className: "custom-date-picker-wrapper", children: [_jsxs(Box, { className: "date-picker-trigger-container", ref: anchorRef, children: [_jsxs(Box, { className: `date-picker-trigger-field ${disabled ? 'disabled-trigger' : ''} ${isOpen && !selectingCompare ? 'active-trigger' : ''}`, onClick: () => {
                            if (disabled)
                                return;
                            if (isOpen && !selectingCompare)
                                handleClose();
                            else
                                handleOpen(false);
                        }, children: [_jsx("div", { className: "d-flex", children: _jsx(CalendarSvg, {}) }), _jsx("span", { className: `date-display-text ${!displayMain ? 'placeholder-text' : ''}`, children: displayMain || placeholder })] }), comparison && compareEnabled && (_jsxs(_Fragment, { children: [_jsx("span", { className: "date-picker-trigger-field vs-separator-trigger", children: "Vs" }), _jsxs(Box, { className: `date-picker-trigger-field ${disabled ? 'disabled-trigger' : ''} ${isOpen && selectingCompare ? 'active-trigger' : ''}`, onClick: () => {
                                    if (disabled)
                                        return;
                                    if (isOpen && selectingCompare)
                                        handleClose();
                                    else
                                        handleOpen(true);
                                }, children: [_jsx("div", { className: "d-flex", children: _jsx(CalendarSvg, {}) }), _jsx("span", { className: `date-display-text ${!displayCompare ? 'placeholder-text' : ''}`, children: displayCompare || comparePlaceholder })] })] }))] }), isOpen && (_jsx(Popper, { open: isOpen, anchorEl: anchorRef.current, placement: "bottom-start", ref: popperRef, className: "date-picker-popper", modifiers: [
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 16, altAxis: true } },
                    { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'], padding: 16 } },
                ], children: _jsx(Paper, { className: "date-picker-modal", elevation: 8, children: _jsxs(Box, { className: "date-picker-content", children: [comparison && (_jsx(Box, { className: "date-picker-tabs", sx: { justifyContent: 'flex-end' }, children: _jsx(Box, { className: "compare-toggle-container", children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: compareEnabled, onChange: handleCompareToggle, className: "compare-switch" }), label: "Compare", className: "compare-label" }) }) })), _jsxs(Box, { className: "date-picker-body", children: [_jsx(Box, { className: "date-picker-sidebar", sx: { display: 'flex', flexDirection: 'column', p: 0 }, children: _jsx(Box, { sx: { flex: '1 1 auto', overflowY: 'auto', p: '10px', maxHeight: '340px' }, children: _jsx(List, { className: "preset-list", children: presets.map((preset, idx) => {
                                                    const disabledPreset = !!preset.requiresMain && !tempMain;
                                                    const isActive = isPresetActive(preset);
                                                    return (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { disabled: disabledPreset, onClick: () => handlePresetClick(preset), className: `preset-item ${isActive ? 'active-preset' : ''}`, sx: { textAlign: 'left !important', justifyContent: 'flex-start !important', width: '100%' }, children: _jsx(Box, { sx: { textAlign: 'left', width: '100%' }, children: preset.label }) }) }, idx));
                                                }) }) }) }), _jsxs(Box, { className: "date-picker-main", children: [_jsxs(Box, { className: "date-range-inputs", children: [_jsx(TextField, { value: formatMonth(tempMain), placeholder: placeholder, InputProps: { readOnly: true, startAdornment: _jsx("div", { className: "d-flex", children: _jsx(CalendarSvg, {}) }) }, className: `range-input-field ${!selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                            setSelectingCompare(false);
                                                            if (tempMain?.year)
                                                                setNavYear(tempMain.year);
                                                        } }), comparison && compareEnabled && (_jsxs(_Fragment, { children: [_jsx("span", { className: "MuiFormControl-root vs-text", children: "Vs" }), _jsx(TextField, { value: formatMonth(tempCompare), placeholder: comparePlaceholder, InputProps: { readOnly: true, startAdornment: _jsx("div", { className: "d-flex", children: _jsx(CalendarSvg, {}) }) }, className: `range-input-field ${selectingCompare ? 'active-input' : ''}`, onClick: () => {
                                                                    setSelectingCompare(true);
                                                                    if (tempCompare?.year)
                                                                        setNavYear(tempCompare.year);
                                                                    else if (tempMain?.year)
                                                                        setNavYear(tempMain.year);
                                                                } })] }))] }), _jsxs(Box, { className: "calendars-container", children: [renderYearPanel(navYear, true, isMobile), !isMobile && renderYearPanel(navYear + 1, false, true)] }), _jsxs(Box, { className: "date-picker-actions", children: [_jsx(AppButton, { onClick: handleClose, className: "cancel-button", children: "Cancel" }), _jsx(AppButton, { onClick: handleApply, className: "apply-button", children: "Apply" })] })] })] })] }) }) }))] }));
};
export default CustomMonthComparePicker;
